import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  validateSession: () => Promise<boolean>;
  forceSignOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const clearDeveloperCache = () => {
    console.log('[AuthContext] Clearing developer-related cache');
    queryClient.removeQueries({ queryKey: ['user-developer-mode'] });
    queryClient.removeQueries({ queryKey: ['suggested-candidates'] });
    queryClient.removeQueries({ queryKey: ['mock-data'] });
    queryClient.invalidateQueries({ queryKey: ['candidates'] });
    queryClient.invalidateQueries({ queryKey: ['jobs'] });
    queryClient.invalidateQueries({ queryKey: ['user-role'] });
  };

  const signOut = async () => {
    console.log('[AuthContext] Sign out initiated');
    try {
      clearDeveloperCache();
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('[AuthContext] Sign out error:', error);
        throw error;
      }
      
      setUser(null);
      setSession(null);
      
      queryClient.clear();
      console.log('[AuthContext] Sign out completed successfully');
      
      // Navigate to home page after successful logout
      window.location.href = '/';
    } catch (error) {
      console.error('[AuthContext] Sign out failed:', error);
      // Still redirect on error to prevent user from staying on protected pages
      window.location.href = '/';
      throw error;
    }
  };

  const refreshSession = async (): Promise<void> => {
    console.log('[AuthContext] Refreshing session');
    try {
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('[AuthContext] Session refresh error:', error);
        throw error;
      }
      
      console.log('[AuthContext] Session refreshed successfully');
      // Don't return data - the auth state change listener will handle updating the context
    } catch (error) {
      console.error('[AuthContext] Session refresh failed:', error);
      throw error;
    }
  };

  const validateSession = async (): Promise<boolean> => {
    console.log('[AuthContext] Validating session');
    try {
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('[AuthContext] Session validation error:', error);
        return false;
      }
      
      const isValid = !!currentSession;
      console.log('[AuthContext] Session validation result:', isValid);
      return isValid;
    } catch (error) {
      console.error('[AuthContext] Session validation failed:', error);
      return false;
    }
  };

  const forceSignOut = async () => {
    console.log('[AuthContext] Force sign out initiated');
    try {
      clearDeveloperCache();
      
      // Force clear local state first
      setUser(null);
      setSession(null);
      
      // Clear query cache
      queryClient.clear();
      
      // Clear localStorage auth data
      try {
        localStorage.removeItem('sb-bdvwkygtrettvwiwbxbc-auth-token');
        localStorage.removeItem('sb-bdvwkygtrettvwiwbxbc-auth-token-code-verifier');
        console.log('[AuthContext] Cleared localStorage auth tokens');
      } catch (storageError) {
        console.warn('[AuthContext] Failed to clear localStorage:', storageError);
      }
      
      // Attempt to sign out from Supabase (but don't throw if it fails)
      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        console.warn('[AuthContext] Force sign out - Supabase signOut failed (continuing anyway):', signOutError);
      }
      
      console.log('[AuthContext] Force sign out completed');
      toast.success('Authentication cleared - please sign in again');
      
      // Navigate to home page after force logout
      window.location.href = '/';
    } catch (error) {
      console.error('[AuthContext] Force sign out failed:', error);
      // Even if force sign out fails, we should clear local state and redirect
      setUser(null);
      setSession(null);
      queryClient.clear();
      toast.error('Authentication recovery failed');
      window.location.href = '/';
      throw error;
    }
  };

  useEffect(() => {
    console.log('[AuthContext] Setting up auth state listener');
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[AuthContext] Auth state changed:', { event, hasSession: !!session });
        
        // Clear developer cache on auth state changes
        if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
          clearDeveloperCache();
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[AuthContext] Error getting initial session:', error);
        } else {
          console.log('[AuthContext] Initial session retrieved:', { hasSession: !!session });
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (error) {
        console.error('[AuthContext] Failed to get initial session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getInitialSession();

    return () => {
      console.log('[AuthContext] Cleaning up auth state listener');
      subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    user,
    session,
    isAuthenticated: !!user && !!session,
    isLoading,
    signOut,
    refreshSession,
    validateSession,
    forceSignOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
